import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasAssistedLiving,
  useRentalManagerUserPropertyEditWebApiHandler_hasBarbecue,
  useRentalManagerUserPropertyEditWebApiHandler_hasBicycleStorage,
  useRentalManagerUserPropertyEditWebApiHandler_hasCeilingFan,
  useRentalManagerUserPropertyEditWebApiHandler_hasDisabledAccess,
  useRentalManagerUserPropertyEditWebApiHandler_hasDryCleaningDropOff,
  useRentalManagerUserPropertyEditWebApiHandler_hasElevator,
  useRentalManagerUserPropertyEditWebApiHandler_hasGuestSuite,
  useRentalManagerUserPropertyEditWebApiHandler_hasHotTub,
  useRentalManagerUserPropertyEditWebApiHandler_hasOnlineMaintenancePortal,
  useRentalManagerUserPropertyEditWebApiHandler_hasOnlineRentPayment,
  useRentalManagerUserPropertyEditWebApiHandler_hasOnsiteManagement,
  useRentalManagerUserPropertyEditWebApiHandler_hasPackageService,
  useRentalManagerUserPropertyEditWebApiHandler_hasPetPark,
  useRentalManagerUserPropertyEditWebApiHandler_hasSauna,
  useRentalManagerUserPropertyEditWebApiHandler_hasSpanishSpeakingStaff,
  useRentalManagerUserPropertyEditWebApiHandler_hasStorageSpace,
  useRentalManagerUserPropertyEditWebApiHandler_hasSwimmingPool,
  useRentalManagerUserPropertyEditWebApiHandler_hasTwentyFourHourMaintenance,
  useRentalManagerUserPropertyEditWebApiHandler_hasValetTrash,
  useRentalManagerUserPropertyEditWebApiHandler_isIncomeRestricted,
  useRentalManagerUserPropertyEditWebApiHandler_isOver55,
  useRentalManagerUserPropertyEditWebApiHandler_smokingAllowed,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import {
  PROPERTY_SERVICE_KEYS,
  PROPERTY_SERVICE_METADATA,
  type PropertyServiceKey,
} from '../../../../constants/KeyFeaturesConstants';

import DisabledAccessAmenityTooltip from '../../../../../srcLegacy/shared/components/DisabledAccessAmenityTooltip';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<PropertyServiceKey, boolean>;

const PropertyServicesAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const buildingAttributes = listingDetails?.buildingAttributes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasTwentyFourHourMaintenance: buildingAttributes?.hasTwentyFourHourMaintenance ?? false,
    hasAssistedLiving: buildingAttributes?.hasAssistedLiving ?? false,
    hasBarbecue: buildingAttributes?.hasBarbecue ?? false,
    hasBicycleStorage: buildingAttributes?.hasBicycleStorage ?? false,
    hasCeilingFan: buildingAttributes?.hasCeilingFan ?? false,
    hasDisabledAccess: buildingAttributes?.hasDisabledAccess ?? false,
    hasDryCleaningDropOff: buildingAttributes?.hasDryCleaningDropOff ?? false,
    hasElevator: buildingAttributes?.hasElevator ?? false,
    hasGuestSuite: buildingAttributes?.hasGuestSuite ?? false,
    hasHotTub: buildingAttributes?.hasHotTub ?? false,
    isIncomeRestricted: buildingAttributes?.isIncomeRestricted ?? false,
    hasOnlineMaintenancePortal: buildingAttributes?.hasOnlineMaintenancePortal ?? false,
    hasOnlineRentPayment: buildingAttributes?.hasOnlineRentPayment ?? false,
    hasOnsiteManagement: buildingAttributes?.hasOnsiteManagement ?? false,
    isOver55: buildingAttributes?.isOver55 ?? false,
    hasPackageService: buildingAttributes?.hasPackageService ?? false,
    hasPetPark: buildingAttributes?.hasPetPark ?? false,
    hasSauna: buildingAttributes?.hasSauna ?? false,
    smokingAllowed: buildingAttributes?.smokingAllowed ?? false,
    hasSpanishSpeakingStaff: buildingAttributes?.hasSpanishSpeakingStaff ?? false,
    hasStorageSpace: buildingAttributes?.hasStorageSpace ?? false,
    hasSwimmingPool: buildingAttributes?.hasSwimmingPool ?? false,
    hasValetTrash: buildingAttributes?.hasValetTrash ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Property services updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateTwentyFourHourMaintenance } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasTwentyFourHourMaintenance,
    skipToast: true,
    meta: { context: { api: 'hasTwentyFourHourMaintenance', details: { listingAlias } } },
  });

  const { mutateAsync: updateAssistedLiving } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasAssistedLiving,
    skipToast: true,
    meta: { context: { api: 'hasAssistedLiving', details: { listingAlias } } },
  });

  const { mutateAsync: updateBarbecue } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasBarbecue,
    skipToast: true,
    meta: { context: { api: 'hasBarbecue', details: { listingAlias } } },
  });

  const { mutateAsync: updateBicycleStorage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasBicycleStorage,
    skipToast: true,
    meta: { context: { api: 'hasBicycleStorage', details: { listingAlias } } },
  });

  const { mutateAsync: updateCeilingFan } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasCeilingFan,
    skipToast: true,
    meta: { context: { api: 'hasCeilingFan', details: { listingAlias } } },
  });

  const { mutateAsync: updateDisabledAccess } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDisabledAccess,
    skipToast: true,
    meta: { context: { api: 'hasDisabledAccess', details: { listingAlias } } },
  });

  const { mutateAsync: updateDryCleaningDropOff } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDryCleaningDropOff,
    skipToast: true,
    meta: { context: { api: 'hasDryCleaningDropOff', details: { listingAlias } } },
  });

  const { mutateAsync: updateElevator } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasElevator,
    skipToast: true,
    meta: { context: { api: 'hasElevator', details: { listingAlias } } },
  });

  const { mutateAsync: updateGuestSuite } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGuestSuite,
    skipToast: true,
    meta: { context: { api: 'hasGuestSuite', details: { listingAlias } } },
  });

  const { mutateAsync: updateHotTub } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasHotTub,
    skipToast: true,
    meta: { context: { api: 'hasHotTub', details: { listingAlias } } },
  });

  const { mutateAsync: updateIncomeRestricted } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_isIncomeRestricted,
    skipToast: true,
    meta: { context: { api: 'isIncomeRestricted', details: { listingAlias } } },
  });

  const { mutateAsync: updateOnlineMaintenancePortal } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOnlineMaintenancePortal,
    skipToast: true,
    meta: { context: { api: 'hasOnlineMaintenancePortal', details: { listingAlias } } },
  });

  const { mutateAsync: updateOnlineRentPayment } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOnlineRentPayment,
    skipToast: true,
    meta: { context: { api: 'hasOnlineRentPayment', details: { listingAlias } } },
  });

  const { mutateAsync: updateOnsiteManagement } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOnsiteManagement,
    skipToast: true,
    meta: { context: { api: 'hasOnsiteManagement', details: { listingAlias } } },
  });

  const { mutateAsync: updateOver55 } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_isOver55,
    skipToast: true,
    meta: { context: { api: 'isOver55', details: { listingAlias } } },
  });

  const { mutateAsync: updatePackageService } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPackageService,
    skipToast: true,
    meta: { context: { api: 'hasPackageService', details: { listingAlias } } },
  });

  const { mutateAsync: updatePetPark } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPetPark,
    skipToast: true,
    meta: { context: { api: 'hasPetPark', details: { listingAlias } } },
  });

  const { mutateAsync: updateSauna } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSauna,
    skipToast: true,
    meta: { context: { api: 'hasSauna', details: { listingAlias } } },
  });

  const { mutateAsync: updateSmokingAllowed } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_smokingAllowed,
    skipToast: true,
    meta: { context: { api: 'smokingAllowed', details: { listingAlias } } },
  });

  const { mutateAsync: updateSpanishSpeakingStaff } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSpanishSpeakingStaff,
    skipToast: true,
    meta: { context: { api: 'hasSpanishSpeakingStaff', details: { listingAlias } } },
  });

  const { mutateAsync: updateStorageSpace } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasStorageSpace,
    skipToast: true,
    meta: { context: { api: 'hasStorageSpace', details: { listingAlias } } },
  });

  const { mutateAsync: updateSwimmingPool } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSwimmingPool,
    skipToast: true,
    meta: { context: { api: 'hasSwimmingPool', details: { listingAlias } } },
  });

  const { mutateAsync: updateValetTrash } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasValetTrash,
    skipToast: true,
    meta: { context: { api: 'hasValetTrash', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateTwentyFourHourMaintenance> = {
    hasTwentyFourHourMaintenance: updateTwentyFourHourMaintenance,
    hasAssistedLiving: updateAssistedLiving,
    hasBarbecue: updateBarbecue,
    hasBicycleStorage: updateBicycleStorage,
    hasCeilingFan: updateCeilingFan,
    hasDisabledAccess: updateDisabledAccess,
    hasDryCleaningDropOff: updateDryCleaningDropOff,
    hasElevator: updateElevator,
    hasGuestSuite: updateGuestSuite,
    hasHotTub: updateHotTub,
    isIncomeRestricted: updateIncomeRestricted,
    hasOnlineMaintenancePortal: updateOnlineMaintenancePortal,
    hasOnlineRentPayment: updateOnlineRentPayment,
    hasOnsiteManagement: updateOnsiteManagement,
    isOver55: updateOver55,
    hasPackageService: updatePackageService,
    hasPetPark: updatePetPark,
    hasSauna: updateSauna,
    smokingAllowed: updateSmokingAllowed,
    hasSpanishSpeakingStaff: updateSpanishSpeakingStaff,
    hasStorageSpace: updateStorageSpace,
    hasSwimmingPool: updateSwimmingPool,
    hasValetTrash: updateValetTrash,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as PropertyServiceKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = PROPERTY_SERVICE_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Property services</Legend>} disabled={isInputFormDisabled}>
      {PROPERTY_SERVICE_KEYS.map((fieldName) => {
        const metadata = PROPERTY_SERVICE_METADATA[fieldName];

        return (
          <LabeledControl
            key={`otherTypes-${metadata.label}`}
            label={
              fieldName === 'hasDisabledAccess' ? (
                <>
                  <Label>{metadata.label}</Label>
                  <DisabledAccessAmenityTooltip />
                </>
              ) : (
                <Label>{metadata.label}</Label>
              )
            }
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(PropertyServicesAmenities);
